"use client"

import React, { useState, useEffect } from "react"
import { useLanguage } from "../contexts/LanguageContext"
import { useToast } from "../components/ui/use-toast"
import { billingAPI, SubscriptionPlan, Subscriber } from "../services/billingService"
import PageHeader from "../components/ui/dashboard/PageHeader"
import { Plus, Search, Calendar, CreditCard, CheckCircle, AlertTriangle, MoreVertical, Trash2, Edit, UserPlus } from "lucide-react"
import {
    Tabs, Card, Flex, Text, Button, Badge, Table, DropdownMenu, TextField, Dialog, Select, Heading, Checkbox, TextArea
} from "@radix-ui/themes"
import EmailTemplatesPage from "./EmailTemplatesPage"
import { restaurantAPI } from "../services/api"

const SubscriptionsPage: React.FC = () => {
    const { t, isRTL } = useLanguage()
    const { toast } = useToast()
    const [activeTab, setActiveTab] = useState("subscribers")

    // State
    const [plans, setPlans] = useState<SubscriptionPlan[]>([])
    const [subscribers, setSubscribers] = useState<Subscriber[]>([])
    const [restaurants, setRestaurants] = useState<any[]>([])
    const [loading, setLoading] = useState(true)

    // Dialog State
    const [showPlanDialog, setShowPlanDialog] = useState(false)
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null)
    const [showAssignDialog, setShowAssignDialog] = useState(false)
    const [showEditSubDialog, setShowEditSubDialog] = useState(false)
    const [selectedSubscription, setSelectedSubscription] = useState<Subscriber | null>(null)

    // Fetch Data
    const fetchData = async () => {
        setLoading(true)
        try {
            const [plansRes, subscribersRes, restaurantsRes] = await Promise.all([
                billingAPI.getPlans(),
                billingAPI.getSubscribers(),
                restaurantAPI.getRestaurants()
            ])
            setPlans(plansRes.data.data || [])
            setSubscribers(subscribersRes.data.data || [])
            setRestaurants(restaurantsRes.data.data || [])
        } catch (error) {
            console.error("Failed to load subscription data", error)
            toast({ title: t("error"), description: "Failed to load subscription data", variant: "destructive" })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const handleConfirmPayment = async (id: string) => {
        try {
            await billingAPI.confirmPayment(id, "MANUAL-" + Date.now())
            toast({ title: t("success"), description: "Payment confirmed successfully" })
            fetchData()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to confirm payment", variant: "destructive" })
        }
    }

    const handleEditPlan = (plan: SubscriptionPlan) => {
        setSelectedPlan(plan)
        setShowPlanDialog(true)
    }

    const handleAddPlan = () => {
        setSelectedPlan(null)
        setShowPlanDialog(true)
    }

    const handleCancelSubscription = async (id: string) => {
        if (!window.confirm(t("confirmCancelSubscription") || "Are you sure you want to cancel this subscription?")) return;
        try {
            await billingAPI.cancelSubscription(id)
            toast({ title: t("success"), description: "Subscription cancelled successfully" })
            fetchData()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to cancel subscription", variant: "destructive" })
        }
    }

    const openEditSubscription = (sub: Subscriber) => {
        setSelectedSubscription(sub)
        setShowEditSubDialog(true)
    }

    const refreshData = () => {
        fetchData()
    }

    const handleAutoEnrollFree = async () => {
        try {
            await billingAPI.checkAndEnrollUnsubscribed()
            toast({ title: t("success"), description: "All unsubscribed restaurants enrolled on free plan" })
            fetchData()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to auto-enroll restaurants", variant: "destructive" })
        }
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={t("subscriptionManagement") || "Subscription Management"}
                subtitle={t("managePlansAndSubscribers") || "Manage plans and restaurant subscriptions"}
            />

            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
                <Tabs.List>
                    <Tabs.Trigger value="subscribers">{t("activeSubscriptions") || "Active Subscriptions"}</Tabs.Trigger>
                    <Tabs.Trigger value="plans">{t("subscriptionPlans") || "Subscription Plans"}</Tabs.Trigger>
                    <Tabs.Trigger value="emails">{t("emailTemplates") || "Email Templates"}</Tabs.Trigger>
                </Tabs.List>

                <div className="mt-4">
                    <Tabs.Content value="subscribers">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Flex justify="between" align="center">
                                    <div className="relative w-64">
                                        <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? "right-3" : "left-3"}`} />
                                        <input
                                            placeholder={t("searchRestaurants") || "Search restaurants..."}
                                            className={`w-full py-2 border rounded-lg ${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} border-gray-300 dark:border-gray-600 bg-transparent`}
                                        />
                                    </div>
                                    <Flex gap="2">
                                        <Button variant="surface" color="indigo" onClick={() => setShowAssignDialog(true)}>
                                            <Plus className="w-4 h-4 mr-2" /> {t("assignPlan") || "Assign Plan"}
                                        </Button>
                                        <Button variant="surface" color="green" onClick={handleAutoEnrollFree}>
                                            <UserPlus className="w-4 h-4 mr-2" /> {t("autoEnrollFree") || "Auto-Enroll Free Plan"}
                                        </Button>
                                        <Button variant="surface">
                                            <Calendar className="w-4 h-4 mr-2" /> {t("filterByDate") || "Filter Date"}
                                        </Button>
                                    </Flex>
                                </Flex>

                                <Table.Root variant="surface">
                                    <Table.Header>
                                        <Table.Row>
                                            <Table.ColumnHeaderCell>{t("restaurant") || "Restaurant"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("plan") || "Plan"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("status") || "Status"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("expiry") || "Expiry"}</Table.ColumnHeaderCell>
                                            <Table.ColumnHeaderCell>{t("actions") || "Actions"}</Table.ColumnHeaderCell>
                                        </Table.Row>
                                    </Table.Header>
                                    <Table.Body>
                                        {subscribers.map(sub => (
                                            <Table.Row key={sub._id}>
                                                <Table.Cell>
                                                    <Text weight="bold">{sub.restaurant?.name}</Text>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={sub.plan?.name === 'Pro' ? 'purple' : 'blue'}>{sub.plan?.name}</Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <Badge color={sub.status === 'active' ? 'green' : sub.status === 'expired' ? 'red' : 'gray'}>
                                                        {sub.status}
                                                    </Badge>
                                                </Table.Cell>
                                                <Table.Cell>
                                                    {new Date(sub.endDate).toLocaleDateString()}
                                                </Table.Cell>
                                                <Table.Cell>
                                                    <DropdownMenu.Root>
                                                        <DropdownMenu.Trigger>
                                                            <Button size="1" variant="soft" color="gray">
                                                                <MoreVertical className="w-4 h-4" />
                                                            </Button>
                                                        </DropdownMenu.Trigger>
                                                        <DropdownMenu.Content>
                                                            <DropdownMenu.Item onClick={() => handleConfirmPayment(sub._id)}>
                                                                <CheckCircle className="w-4 h-4 mr-2 text-green-500" /> {t("confirmPay") || "Confirm Payment"}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Item onClick={() => openEditSubscription(sub)}>
                                                                <Edit className="w-4 h-4 mr-2 text-blue-500" /> {t("editSubscription") || "Edit Subscription"}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Separator />
                                                            <DropdownMenu.Item color="red" onClick={() => handleCancelSubscription(sub._id)}>
                                                                <Trash2 className="w-4 h-4 mr-2" /> {t("cancelTheme") || "Cancel Subscription"}
                                                            </DropdownMenu.Item>
                                                        </DropdownMenu.Content>
                                                    </DropdownMenu.Root>
                                                </Table.Cell>
                                            </Table.Row>
                                        ))}
                                        {subscribers.length === 0 && !loading && (
                                            <Table.Row>
                                                <Table.Cell colSpan={5} align="center">
                                                    <Text color="gray">{t("noSubscriptionsFound") || "No subscriptions found"}</Text>
                                                </Table.Cell>
                                            </Table.Row>
                                        )}
                                    </Table.Body>
                                </Table.Root>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="plans">
                        <Card>
                            <Flex direction="column" gap="4">
                                <Flex justify="end">
                                    <Button onClick={handleAddPlan}>
                                        <Plus className="w-4 h-4 mr-2" /> {t("addNewPlan") || "Add New Plan"}
                                    </Button>
                                </Flex>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {plans.map(plan => (
                                        <Card key={plan._id} className="hover:shadow-lg transition-shadow">
                                            <Flex direction="column" gap="3">
                                                <Flex justify="between" align="center">
                                                    <Heading size="4">{plan.name}</Heading>
                                                    <Badge color={plan.isActive ? 'green' : 'gray'}>{plan.isActive ? 'Active' : 'Inactive'}</Badge>
                                                </Flex>
                                                <Flex direction="column">
                                                    <Text size="6" weight="bold">SDG {plan.pricing?.monthly || plan.price || 0}<span className="text-sm font-normal text-gray-500">/mo</span></Text>
                                                    <Text size="4" weight="bold" color="gray">SDG {plan.pricing?.yearly || 0}<span className="text-sm font-normal text-gray-500">/yr</span></Text>
                                                </Flex>
                                                <ul className="space-y-2 my-2">
                                                    {plan.features.map((feat, i) => (
                                                        <li key={i} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                                            <CheckCircle className="w-3 h-3 mr-2 text-green-500" /> {feat}
                                                        </li>
                                                    ))}
                                                </ul>
                                                <Flex gap="2" className="mt-2">
                                                    <Button variant="soft" className="flex-1" onClick={() => handleEditPlan(plan)}><Edit className="w-3 h-3 mr-1" /> {t("edit") || "Edit"}</Button>
                                                    <Button variant="soft" color="red"><Trash2 className="w-3 h-3" /></Button>
                                                </Flex>
                                            </Flex>
                                        </Card>
                                    ))}
                                </div>
                            </Flex>
                        </Card>
                    </Tabs.Content>

                    <Tabs.Content value="emails">
                        <EmailTemplatesPage />
                    </Tabs.Content>
                </div>
            </Tabs.Root>

            <PlanDialog
                open={showPlanDialog}
                onOpenChange={setShowPlanDialog}
                plan={selectedPlan}
                onSave={refreshData}
            />

            <AssignPlanDialog
                open={showAssignDialog}
                onOpenChange={setShowAssignDialog}
                plans={plans}
                restaurants={restaurants}
                onSave={refreshData}
            />

            <EditSubscriptionDialog
                open={showEditSubDialog}
                onOpenChange={setShowEditSubDialog}
                subscription={selectedSubscription}
                plans={plans}
                onSave={refreshData}
            />
        </div>
    )
}

interface PlanDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    plan?: SubscriptionPlan | null
    onSave: () => void
}

const PlanDialog: React.FC<PlanDialogProps> = ({ open, onOpenChange, plan, onSave }) => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [loading, setLoading] = useState(false)

    // State for the form
    const [formData, setFormData] = useState({
        name: plan?.name || "",
        nameAr: plan?.nameAr || "",
        monthlyPrice: plan?.pricing?.monthly || plan?.price || 0,
        yearlyPrice: plan?.pricing?.yearly || 0,
        features: plan?.features ? plan.features.join("\n") : "",
        featuresAr: plan?.featuresAr ? plan.featuresAr.join("\n") : "",
    })

    useEffect(() => {
        if (open) {
            setFormData({
                name: plan?.name || "",
                nameAr: plan?.nameAr || "",
                monthlyPrice: plan?.pricing?.monthly || plan?.price || 0,
                yearlyPrice: plan?.pricing?.yearly || 0,
                features: plan?.features ? plan.features.join("\n") : "",
                featuresAr: plan?.featuresAr ? plan.featuresAr.join("\n") : "",
            })
        }
    }, [open, plan])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        const payload = {
            name: formData.name,
            nameAr: formData.nameAr,
            pricing: {
                monthly: Number(formData.monthlyPrice),
                yearly: Number(formData.yearlyPrice)
            },
            features: formData.features.split("\n").map(f => f.trim()).filter(Boolean),
            featuresAr: formData.featuresAr.split("\n").map(f => f.trim()).filter(Boolean),
            isActive: true
        }

        try {
            if (plan) {
                await billingAPI.updatePlan(plan._id, payload)
                toast({ title: t("success"), description: t("planUpdated") || "Plan updated successfully" })
            } else {
                await billingAPI.createPlan(payload)
                toast({ title: t("success"), description: t("planCreated") || "Plan created successfully" })
            }
            onOpenChange(false)
            onSave()
        } catch (error) {
            toast({ title: t("error"), description: t("failedToSavePlan") || "Failed to save plan", variant: "destructive" })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Content style={{ maxWidth: 500 }}>
                <Dialog.Title>{plan ? (t("editPlan") || "Edit Plan") : (t("addNewPlan") || "Add New Plan")}</Dialog.Title>
                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="3">
                        <Tabs.Root defaultValue="en">
                            <Tabs.List>
                                <Tabs.Trigger value="en">English</Tabs.Trigger>
                                <Tabs.Trigger value="ar">Arabic</Tabs.Trigger>
                            </Tabs.List>
                            <div className="mt-4">
                                <Tabs.Content value="en">
                                    <Flex direction="column" gap="3">
                                        <label>
                                            <Text as="div" size="2" mb="1" weight="bold">{t("planName") || "Plan Name"}</Text>
                                            <TextField.Root value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Pro Plan" required />
                                        </label>
                                        <label>
                                            <Text as="div" size="2" mb="1" weight="bold">{t("features") || "Features (one per line)"}</Text>
                                            <TextArea value={formData.features} onChange={e => setFormData(f => ({ ...f, features: e.target.value }))} placeholder="Feature 1&#10;Feature 2" rows={4} required />
                                        </label>
                                    </Flex>
                                </Tabs.Content>
                                <Tabs.Content value="ar">
                                    <Flex direction="column" gap="3">
                                        <label>
                                            <Text as="div" size="2" mb="1" weight="bold">{t("planNameAr") || "Plan Name (Arabic)"}</Text>
                                            <TextField.Root value={formData.nameAr} onChange={e => setFormData(f => ({ ...f, nameAr: e.target.value }))} placeholder="الخطة الاحترافية" dir="rtl" />
                                        </label>
                                        <label>
                                            <Text as="div" size="2" mb="1" weight="bold">{t("featuresAr") || "Features Arabic (one per line)"}</Text>
                                            <TextArea value={formData.featuresAr} onChange={e => setFormData(f => ({ ...f, featuresAr: e.target.value }))} placeholder="ميزة 1&#10;ميزة 2" rows={4} dir="rtl" />
                                        </label>
                                    </Flex>
                                </Tabs.Content>
                            </div>
                        </Tabs.Root>

                        <Flex gap="3" mt="2">
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("monthlyPrice") || "Monthly Price"}</Text>
                                <TextField.Root value={formData.monthlyPrice} onChange={e => setFormData(f => ({ ...f, monthlyPrice: Number(e.target.value) }))} type="number" placeholder="0.00" required>
                                    <TextField.Slot>$</TextField.Slot>
                                </TextField.Root>
                            </label>
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("yearlyPrice") || "Yearly Price"}</Text>
                                <TextField.Root value={formData.yearlyPrice} onChange={e => setFormData(f => ({ ...f, yearlyPrice: Number(e.target.value) }))} type="number" placeholder="0.00" required>
                                    <TextField.Slot>$</TextField.Slot>
                                </TextField.Root>
                            </label>
                        </Flex>

                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button type="submit" disabled={loading}>{loading ? (t("saving") || "Saving...") : (t("save") || "Save")}</Button>
                        </Flex>
                    </Flex>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    )
}

interface AssignPlanDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    plans: SubscriptionPlan[]
    restaurants: any[]
    onSave: () => void
}

const AssignPlanDialog: React.FC<AssignPlanDialogProps> = ({ open, onOpenChange, plans, restaurants, onSave }) => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        restaurantId: "",
        planId: "",
        billingCycle: "monthly",
        startDate: "",
        endDate: "",
        status: "active"
    })

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        try {
            await billingAPI.assignPlan(formData)
            toast({ title: t("success"), description: "Plan assigned successfully" })
            onOpenChange(false)
            onSave()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to assign plan", variant: "destructive" })
        } finally {
            setLoading(false)
        }
    }

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Content style={{ maxWidth: 450 }}>
                <Dialog.Title>{t("assignPlanToRestaurant") || "Assign Plan to Restaurant"}</Dialog.Title>
                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("restaurant") || "Restaurant"}</Text>
                            <Select.Root value={formData.restaurantId} onValueChange={v => setFormData(f => ({ ...f, restaurantId: v }))}>
                                <Select.Trigger placeholder="Select a restaurant" className="w-full" />
                                <Select.Content>
                                    <Select.Group>
                                        {restaurants.map(r => (
                                            <Select.Item key={r._id} value={r._id}>{r.name}</Select.Item>
                                        ))}
                                    </Select.Group>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("subscriptionPlan") || "Subscription Plan"}</Text>
                            <Select.Root value={formData.planId} onValueChange={v => setFormData(f => ({ ...f, planId: v }))}>
                                <Select.Trigger placeholder="Select a plan" className="w-full" />
                                <Select.Content>
                                    <Select.Group>
                                        {plans.map(p => (
                                            <Select.Item key={p._id} value={p._id}>{p.name}</Select.Item>
                                        ))}
                                    </Select.Group>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("billingCycle") || "Billing Cycle"}</Text>
                            <Select.Root value={formData.billingCycle} onValueChange={v => setFormData(f => ({ ...f, billingCycle: v }))}>
                                <Select.Trigger className="w-full" />
                                <Select.Content>
                                    <Select.Group>
                                        <Select.Item value="monthly">Monthly</Select.Item>
                                        <Select.Item value="yearly">Yearly</Select.Item>
                                    </Select.Group>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <Flex gap="3">
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("startDate") || "Start Date"}</Text>
                                <TextField.Root type="date" value={formData.startDate} onChange={e => setFormData(f => ({ ...f, startDate: e.target.value }))} />
                            </label>
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("endDate") || "End Date"}</Text>
                                <TextField.Root type="date" value={formData.endDate} onChange={e => setFormData(f => ({ ...f, endDate: e.target.value }))} />
                            </label>
                        </Flex>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button type="submit" disabled={loading || !formData.restaurantId || !formData.planId}>
                                {loading ? t("saving") : t("assign")}
                            </Button>
                        </Flex>
                    </Flex>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    )
}

interface EditSubscriptionDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    subscription: Subscriber | null
    plans: SubscriptionPlan[]
    onSave: () => void
}

const EditSubscriptionDialog: React.FC<EditSubscriptionDialogProps> = ({ open, onOpenChange, subscription, plans, onSave }) => {
    const { t } = useLanguage()
    const { toast } = useToast()
    const [loading, setLoading] = useState(false)
    const [formData, setFormData] = useState({
        plan: "",
        status: "",
        startDate: "",
        endDate: "",
        autoRenew: false
    })

    useEffect(() => {
        if (subscription && open) {
            setFormData({
                plan: subscription.plan?._id,
                status: subscription.status,
                startDate: subscription.startDate ? new Date(subscription.startDate).toISOString().split('T')[0] : "",
                endDate: subscription.endDate ? new Date(subscription.endDate).toISOString().split('T')[0] : "",
                autoRenew: subscription.autoRenew || false
            })
        }
    }, [subscription, open])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!subscription) return
        setLoading(true)
        try {
            await billingAPI.updateSubscription(subscription._id, formData)
            toast({ title: t("success"), description: "Subscription updated successfully" })
            onOpenChange(false)
            onSave()
        } catch (error) {
            toast({ title: t("error"), description: "Failed to update subscription", variant: "destructive" })
        } finally {
            setLoading(false)
        }
    }

    if (!subscription) return null;

    return (
        <Dialog.Root open={open} onOpenChange={onOpenChange}>
            <Dialog.Content style={{ maxWidth: 450 }}>
                <Dialog.Title>{t("editSubscription") || "Edit Subscription"} - {subscription.restaurant?.name}</Dialog.Title>
                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="3">
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("subscriptionPlan") || "Subscription Plan"}</Text>
                            <Select.Root value={formData.plan} onValueChange={v => setFormData(f => ({ ...f, plan: v }))}>
                                <Select.Trigger className="w-full" />
                                <Select.Content>
                                    <Select.Group>
                                        {plans.map(p => (
                                            <Select.Item key={p._id} value={p._id}>{p.name}</Select.Item>
                                        ))}
                                    </Select.Group>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <label>
                            <Text as="div" size="2" mb="1" weight="bold">{t("status") || "Status"}</Text>
                            <Select.Root value={formData.status} onValueChange={v => setFormData(f => ({ ...f, status: v }))}>
                                <Select.Trigger className="w-full" />
                                <Select.Content>
                                    <Select.Group>
                                        <Select.Item value="active">Active</Select.Item>
                                        <Select.Item value="trial">Trial</Select.Item>
                                        <Select.Item value="free">Free</Select.Item>
                                        <Select.Item value="expired">Expired</Select.Item>
                                        <Select.Item value="cancelled">Cancelled</Select.Item>
                                    </Select.Group>
                                </Select.Content>
                            </Select.Root>
                        </label>
                        <Flex gap="3">
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("startDate") || "Start Date"}</Text>
                                <TextField.Root type="date" value={formData.startDate} onChange={e => setFormData(f => ({ ...f, startDate: e.target.value }))} />
                            </label>
                            <label className="flex-1">
                                <Text as="div" size="2" mb="1" weight="bold">{t("endDate") || "End Date"}</Text>
                                <TextField.Root type="date" value={formData.endDate} onChange={e => setFormData(f => ({ ...f, endDate: e.target.value }))} />
                            </label>
                        </Flex>
                        <Text as="label" size="2">
                            <Flex gap="2" align="center">
                                <Checkbox checked={formData.autoRenew} onCheckedChange={(c) => setFormData(f => ({ ...f, autoRenew: c === true }))} />
                                {t("autoRenew") || "Auto Renew"}
                            </Flex>
                        </Text>
                        <Flex gap="3" mt="4" justify="end">
                            <Dialog.Close>
                                <Button variant="soft" color="gray">{t("cancel") || "Cancel"}</Button>
                            </Dialog.Close>
                            <Button type="submit" disabled={loading}>
                                {loading ? t("saving") : t("save")}
                            </Button>
                        </Flex>
                    </Flex>
                </form>
            </Dialog.Content>
        </Dialog.Root>
    )
}

export default SubscriptionsPage
